import React, {
  ReactElement,
} from 'react';
import {
  DatePicker, FormGroup, Label, Select,
} from 'idsk-react-components';
import { TypCiselnikaEnum } from 'ciselniky-frontend-ts-axios';
import { ExpansionPanel } from 'slovlex-idsk-react-components';
import CiselnikPolozkaSelect from './CiselnikPolozkaSelect';
import MultiSelectWithTreeForOblast from '../../../MultiSelectWithTreeForOblast';
import {
  lpStadiumMaterialuValues,
  piStadiumMaterialuValues,
} from '../../../../model/legislativnyMaterial/StadiumMaterialu';
import { SafeParameterSetter } from '../../../../types/SafeParameterSetter';
import PolozkaCiselnik from '../../../../model/ciselniky/PolozkaCiselnik';
import LegMaterialListFilter from '../../../../model/legislativnyMaterial/LegMaterialListFilter';
import SearchInput from './SearchInput';
import './sidePanel.scss';
import InstituciaSingleSelect from './InstituciaSingleSelect';
import PredkladatelSingleSelect from './PredkladatelSingleSelect';

interface Props {
  isPredbezna?: boolean;
  filter: LegMaterialListFilter;
  onUpdateField?: SafeParameterSetter<LegMaterialListFilter>;
  filterCopy: LegMaterialListFilter;
  onUpdateFieldCopy: SafeParameterSetter<LegMaterialListFilter>;
  setFilter: (filter: LegMaterialListFilter) => void;
}

const SidePanel = ({
  isPredbezna = false,
  filter,
  onUpdateField,
  filterCopy,
  onUpdateFieldCopy,
  setFilter,
}: Props): ReactElement => {
  const stadiumList = isPredbezna ? piStadiumMaterialuValues : lpStadiumMaterialuValues;
  return (
    <div className="legislativny-material-list-side-panel">
      <div className="slovlex-idsk-side-panel-input-wrapper stadium-procesu-container">
        <Label className="blue-label" htmlFor="stadium">Štádium materiálu</Label>
        <Select
          id="stadium"
          name="stadium"
          width="full"
          onChange={({ target }) => {
            onUpdateField('stadium', target.value === 'Vyberte' ? null : target.value);
          }}
          value={filter.stadium ? filter.stadium : ''}
        >
          <option>Vyberte</option>
          {(Array.from(stadiumList) && Array.from(stadiumList)?.length > 0) ? (
            <>
              {Array.from(stadiumList)
                .sort(([, aStadium], [, bStadium]) => aStadium.nazov.localeCompare(bStadium.nazov))
                .map(([stadiumEnum, stadium]) => (
                  <option key={stadiumEnum} value={stadiumEnum}>{stadium.nazov}</option>
                ))}
            </>
          ) : (
            <option key="emptyOption" value="emptyOption" disabled>
              Nenašiel sa žiadny záznam.
            </option>
          )}
        </Select>
      </div>
      <SearchInput
        filterCopy={filterCopy}
        label="Hľadaný výraz"
        filterSearchText={filterCopy?.searchTextVyraz}
        fieldName="searchTextVyraz"
        onUpdateFieldCopy={onUpdateFieldCopy}
        setFilter={setFilter}
        className="slovlex-idsk-side-panel-input-wrapper"
      />
      <SearchInput
        filterCopy={filterCopy}
        label="Číslo procesu"
        filterSearchText={filterCopy?.searchTextCislo}
        fieldName="searchTextCislo"
        onUpdateFieldCopy={onUpdateFieldCopy}
        setFilter={setFilter}
        className="slovlex-idsk-side-panel-input-wrapper"
      />
      <SearchInput
        filterCopy={filterCopy}
        filterSearchText={filterCopy?.searchTextNazov}
        label="Názov materiálu"
        fieldName="searchTextNazov"
        onUpdateFieldCopy={onUpdateFieldCopy}
        setFilter={setFilter}
        className="slovlex-idsk-side-panel-input-wrapper"
        ariaLabel="Názov materiálu"
      />

      {!isPredbezna && (
        <div className="slovlex-idsk-side-panel-input-wrapper">
          <CiselnikPolozkaSelect
            setTyp={(newValue: PolozkaCiselnik) => onUpdateField('typ', newValue)}
            selectedTypKod={filter?.typ?.kod}
            typCiselnikaEnum={TypCiselnikaEnum.TypMaterialu}
            label="Typ materiálu"
            fieldName="typMaterialu"
            predbeznaInformacia={isPredbezna}
          />
        </div>
      )}
      <MultiSelectWithTreeForOblast
        oblastList={filter.oblastList}
        onUpdateField={onUpdateField}
        fazet
        showItemList={false}
      />
      <ExpansionPanel
        title="Rezortné číslo"
        isExpanded={filter?.searchTextNazov !== ''}
      >
        <SearchInput
          filterCopy={filterCopy}
          filterSearchText={filterCopy?.searchTextRezort}
          fieldName="searchTextRezort"
          onUpdateFieldCopy={onUpdateFieldCopy}
          setFilter={setFilter}
          ariaLabel="Rezortné číslo"
        />
      </ExpansionPanel>
      <ExpansionPanel
        title="Ročník"
        isExpanded={filter?.searchTextRocnik !== ''}
      >
        <SearchInput
          filterCopy={filterCopy}
          filterSearchText={filterCopy?.searchTextRocnik}
          fieldName="searchTextRocnik"
          onUpdateFieldCopy={onUpdateFieldCopy}
          setFilter={setFilter}
          type="search-number"
          maxLength={4}
          ariaLabel="Ročník"
        />
      </ExpansionPanel>
      <PredkladatelSingleSelect
        onUpdateField={onUpdateField}
        onUpdateFieldCopy={onUpdateFieldCopy}
        filterCopy={filterCopy}
        filter={filter}
      />
      <InstituciaSingleSelect
        onUpdateField={onUpdateField}
        onUpdateFieldCopy={onUpdateFieldCopy}
        filterCopy={filterCopy}
        filter={filter}
      />
      <ExpansionPanel
        title="Dátum zmeny"
        isExpanded={!!filter.datumZmenyOd || !!filter.datumZmenyDo}
      >
        <FormGroup>
          <FormGroup>
            <Label htmlFor="zmenaOd">Zmena od</Label>
            <DatePicker
              id="zmenaOd"
              showIcon
              value={filter?.datumZmenyOd}
              onChange={date => onUpdateField('datumZmenyOd', date)}
              strictParsing
              ariaLabel="Zmena od"
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="zmenaOd">Zmena do</Label>
            <DatePicker
              id="zmenaDo"
              showIcon
              value={filter?.datumZmenyDo}
              onChange={date => onUpdateField('datumZmenyDo', date)}
              strictParsing
              ariaLabel="Zmena do"
            />
          </FormGroup>
        </FormGroup>
      </ExpansionPanel>
    </div>
  );
};

export default SidePanel;
